import { z } from 'zod';
import {
  adminCreateUserBaseSchema,
  emailSchema,
  validateDemoExpirationRequirement,
} from './auth';
import { accountTypeSchema } from './quota';
import { companyIdSchema } from './company';
import { ORG_ERROR_CODES } from '../constants/error-codes';

export const organizationRoleSchema = z.enum(['owner', 'admin', 'member'], {
  error: 'Le rôle dans l’entreprise est invalide.',
});

export const ORG_PERMISSION_STATEMENTS = {
  assistantSettings: ['read', 'update'],
  companyBranding: ['read', 'update'],
  companyMember: ['create', 'read', 'update', 'deactivate', 'manageQuota'],
  datasource: ['create', 'read', 'manage', 'delete'],
  knowledgeBase: ['upload', 'read', 'delete'],
  companyUsage: ['read'],
} as const;

export const ORG_ROLE_PERMISSIONS = {
  owner: ORG_PERMISSION_STATEMENTS,
  admin: ORG_PERMISSION_STATEMENTS,
  member: {
    assistantSettings: [],
    companyBranding: ['read'],
    companyMember: [],
    datasource: [],
    knowledgeBase: [],
    companyUsage: [],
  },
} as const;

export const companyManageableRoleSchema = z.enum(['admin', 'member'], {
  error: 'Le rôle du membre est invalide.',
});

export const companyCreateMemberSchema = adminCreateUserBaseSchema
  .omit({ companyId: true, organizationRole: true, role: true })
  .extend({
    role: companyManageableRoleSchema.default('member'),
  })
  .superRefine(validateDemoExpirationRequirement);

export const companyChangeMemberRoleSchema = z.object({
  role: companyManageableRoleSchema,
});

export const companyMemberParamsSchema = z.object({
  userId: z.string().trim().min(1, {
    error: 'L’identifiant utilisateur est requis.',
  }),
});

export const adminCompanyParamsSchema = z.object({
  companyId: companyIdSchema,
});

export const companyMembersQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(20),
  role: z.enum(['all', 'admin', 'member']).default('all'),
  search: z.string().trim().max(120).optional().transform((value) => value || undefined),
  sortBy: z.enum([
    'createdAt',
    'email',
    'emailVerified',
    'name',
    'role',
    'status',
    'updatedAt',
  ], {
    error: 'Le champ de tri des membres est invalide.',
  }).default('createdAt'),
  sortDirection: z.enum(['asc', 'desc'], {
    error: 'La direction de tri des membres est invalide.',
  }).default('desc'),
  status: z.enum(['all', 'active', 'disabled']).default('all'),
  verification: z.enum(['all', 'pending', 'verified'], {
    error: 'Le filtre de vérification est invalide.',
  }).default('all'),
});

export const companyDeactivateMemberSchema = z.object({
  reason: z.string().trim().max(240, {
    error: 'Le motif de désactivation est trop long.',
  }).optional(),
});

export const companyMemberSchema = z.object({
  accountType: accountTypeSchema,
  banned: z.boolean(),
  createdAt: z.iso.datetime(),
  email: emailSchema,
  emailVerified: z.boolean(),
  id: z.string().min(1),
  name: z.string().min(1),
  role: organizationRoleSchema,
  /** Cumul des tokens consommés par le membre, tous usages confondus. */
  tokensConsumed: z.number().int().min(0).default(0),
  updatedAt: z.iso.datetime(),
});

export const paginatedCompanyMembersSchema = z.object({
  items: z.array(companyMemberSchema),
  meta: z.object({
    page: z.number().int().min(1),
    pageCount: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
  }),
});

export const companyMemberCreatedSchema = z.object({
  data: z.object({
    companyId: companyIdSchema,
    companyName: z.string().trim().min(1),
    invitation: z.discriminatedUnion('status', [
      z.object({ status: z.literal('sent') }),
      z.object({
        code: z.literal(ORG_ERROR_CODES.INVITATION_SEND_FAILED),
        status: z.literal('failed'),
      }),
    ]),
    userId: z.string().trim().min(1),
  }),
  success: z.literal(true),
});

export const companyMemberInvitationResponseSchema = z.object({
  data: z.object({ status: z.literal('sent') }),
  success: z.literal(true),
});

export const adminCompanyMemberParamsSchema = adminCompanyParamsSchema.extend({
  userId: companyMemberParamsSchema.shape.userId,
});

export type OrganizationRole = z.infer<typeof organizationRoleSchema>;
export type CompanyManageableRole = z.infer<typeof companyManageableRoleSchema>;
export type CompanyCreateMemberDTO = z.infer<typeof companyCreateMemberSchema>;
export type CompanyMemberDTO = z.infer<typeof companyMemberSchema>;
export type CompanyMemberCreatedDTO = z.infer<typeof companyMemberCreatedSchema>;
export type CompanyMemberInvitationResponseDTO = z.infer<
  typeof companyMemberInvitationResponseSchema
>;
export type CompanyMembersQueryDTO = z.infer<typeof companyMembersQuerySchema>;
export type PaginatedCompanyMembersDTO = z.infer<typeof paginatedCompanyMembersSchema>;
export type OrgPermissionStatements = typeof ORG_PERMISSION_STATEMENTS;
export type OrgPermissionResource = keyof OrgPermissionStatements;
export type OrgPermissionAction<Resource extends OrgPermissionResource> =
  OrgPermissionStatements[Resource][number];
